// Dashboard.jsx - TypeAce Modern Dashboard (UI Redesigned)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ResultCard from "../src/components/ResultCard";
import axios from 'axios';
import {
  Users,
  FileText,
  Zap,
  Award,
  Search,
  Filter,
  Download,
  ChevronRight,
  Clock,
  Calendar,
  User,
  LogOut,
  TrendingUp,
  TrendingDown,
  MoreHorizontal,
  Sun,
  Moon,
  Zap as ZapIcon,
  CheckCircle,
  XCircle,
  AlertCircle,
  GraduationCap,
  Mail,
  CalendarDays,
  Layers,
  ChevronDown // Added for subtle dropdown indicator
} from "lucide-react";
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => {return localStorage.getItem("typeace-theme") || 'light'});
  const [institution, setInstitution] = useState({ name: 'Stanford University', college_code: 'STNFRD' });
  const [stats, setStats] = useState({
    total_students: 1248,
    total_tests: 3542,
    avg_wpm: 52,
    pass_rate: 78
  });
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSearch, setShowSearch] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const updateResult = async (id, passed) => {
    try{
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_API_URL}/admin/result/update`,
    {
      resultId:id,
      passed:passed
    },
    {
      headers:{
        Authorization:`Bearer ${token}`
      }
    });
      fetchDashboardData();
      setShowModal(false);
    }catch(err){
      console.error(err);
    }
  };

  const exportExcel = async () => {
    try{
      const token = localStorage.getItem("typeace_token") || sessionStorage.getItem("typeace_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/export`,
        {
          headers:{
            Authorization:`Bearer ${token}`
          },
          responseType:"blob"
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download","typeace_results.xlsx");
      document.body.appendChild(link);
      link.click();
    }catch(err){
      console.error(err);
    }
  };

  const fetchResultDetails = async (result) => {
    try{
      const token = localStorage.getItem("typeace_token") || sessionStorage.getItem("typeace_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/result/${result.id}`,
        {
          headers:{
            Authorization:`Bearer ${token}`
          }
        }
      );
      if(res.data.success){
        setSelectedResult(res.data.result);
        setShowModal(true);
      }
    }catch(err){
      console.error(err);
    }
    };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("typeace_token") || sessionStorage.getItem("typeace_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (response.data.success) {
        setInstitution(response.data.institution);
        setStats(response.data.stats);
        setResults(response.data.results);
        setFilteredResults(response.data.results);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-ta-theme', theme);
    localStorage.setItem("typeace-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredResults(results);
    } else {
      const filtered = results.filter(result => 
        result.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredResults(filtered);
    }
  }, [searchTerm, results]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDay = () => {
    return currentTime.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const toggleTheme = () => {
    const themes = ['light', 'dark', 'neon'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const newTheme = themes[nextIndex];
    setTheme(newTheme);
    localStorage.setItem("typeace-theme", newTheme);
  };

  const StatCard = ({ icon: Icon, label, value, trend, trendValue, subtitle, color = 'primary' }) => (
    <div className={`ta-stat-card ta-stat-card-${color}`}>
      <div className="ta-stat-card-inner">
        <div className="ta-stat-header">
          <div className={`ta-stat-icon ta-stat-icon-${color}`}>
            <Icon size={20} />
          </div>
          {trend && (
            <div className={`ta-stat-trend ta-trend-${trend}`}>
              {trend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className="ta-stat-content">
          <span className="ta-stat-label">{label}</span>
          <div className="ta-stat-main">
            <span className="ta-stat-value">{value}</span>
            {subtitle && <span className="ta-stat-subtitle">{subtitle}</span>}
          </div>
        </div>
      </div>
    </div>
  );

  const MetricCard = ({ label, value, trend, trendValue }) => (
    <div className="ta-metric-card">
      <div className="ta-metric-label">{label}</div>
      <div className="ta-metric-value-wrapper">
        <span className="ta-metric-value">{value}</span>
        {trend && (
          <div className={`ta-metric-trend ta-metric-trend-${trend}`}>
            {trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
    </div>
  );

  const ThemeIcon = () => {
    switch(theme) {
      case 'light': return <Sun size={18} />;
      case 'dark': return <Moon size={18} />;
      case 'neon': return <ZapIcon size={18} />;
      default: return <Sun size={18} />;
    }
  };

  if (loading) {
    return (
      <div className="ta-loading-screen">
        <div className="ta-loader"></div>
        <p>Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div className={`ta-dashboard ta-theme-${theme}`}>
      {/* Sticky Glass Top Navigation */}
      <nav className="ta-navbar">
        <div className="ta-nav-left">
          <div className="ta-logo">
            <div className="ta-logo-mark">
              <Zap size={24} />
            </div>
            <span className="ta-logo-text" onClick={() => {navigate("/")}}>TypeAce</span>
          </div>
          <div className="ta-nav-links">
            <a href="#" className="ta-nav-link ta-active">Dashboard</a>
            <a href="#" className="ta-nav-link">Analytics</a>
            <a href="#" className="ta-nav-link">Results</a>
            <a href="#" className="ta-nav-link">Settings</a>
          </div>
        </div>

        <div className="ta-nav-right">
          <div className="ta-time-badge">
            <Clock size={16} />
            <span>{formatTime()}</span>
          </div>
          <button className="ta-theme-toggle" onClick={toggleTheme}>
            <ThemeIcon />
          </button>
          <button className="ta-user-menu" onClick={() => {}}>
            <div className="ta-user-avatar">
              <User size={16} />
            </div>
            <ChevronDown size={16} />
          </button>
        </div>
      </nav>

      {/* Main Content Area - Full Width */}
      <main className="ta-main">
        <div className="ta-content">
          {/* Page Header */}
          <header className="ta-page-header">
            <div className="ta-header-left">
              <div className="ta-breadcrumb">
                <span>Dashboard</span>
                <ChevronRight size={14} />
                <span className="ta-breadcrumb-current">Overview</span>
              </div>
              <h1 className="ta-page-title">Institution overview</h1>
              <div className="ta-institution-info">
                <GraduationCap size={16} />
                <span>{institution.name}</span>
                <span className="ta-info-separator">•</span>
                <span className="ta-institution-code">{institution.college_code}</span>
              </div>
            </div>
            <div className="ta-header-right">
              <div className="ta-date-badge">
                <Calendar size={16} />
                <span>{formatDay()}</span>
              </div>
              <button 
                className="ta-button ta-button-ghost"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  navigate('/login');
                }}
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </header>

          {/* Stats Grid */}
          <div className="ta-stats-grid">
            <StatCard 
              icon={Users}
              label="Total students"
              value={stats.total_students.toLocaleString()}
              color="blue"
            />
            <StatCard 
              icon={FileText}
              label="Tests taken"
              value={stats.total_tests.toLocaleString()}
              color="green"
            />
            <StatCard 
              icon={Award}
              label="Pass rate"
              value={`${stats.pass_rate}%`}
              color="purple"
            />
          </div>
          {/* Results Table */}
          <section className="ta-table-section">
            <div className="ta-section-header">
              <div className="ta-section-title">
                <Layers size={20} />
                <h2>Recent test results</h2>
                <span className="ta-section-badge">{filteredResults.length} records</span>
              </div>
              <div className="ta-table-actions">
                <div className={`ta-search ${showSearch ? 'ta-search-expanded' : ''}`}>
                  {showSearch && (
                    <div className="ta-search-field">
                      <Search size={16} className="ta-search-field-icon" />
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="ta-search-input"
                        autoFocus
                      />
                    </div>
                  )}
                  <button 
                    className="ta-icon-button"
                    onClick={() => setShowSearch(!showSearch)}
                  >
                    <Search size={18} />
                  </button>
                </div>
                <button className="ta-icon-button">
                  <Filter size={18} />
                </button>
                <button className="ta-button ta-button-primary" onClick={exportExcel}>
                  <Download size={16} />
                  <span>Export</span>
                </button>
                <button className="ta-icon-button">
                  <MoreHorizontal size={18} />
                </button>
              </div>
            </div>

            {filteredResults.length === 0 ? (
              <div className="ta-empty-state">
                <div className="ta-empty-icon">
                  <AlertCircle size={48} />
                </div>
                <h3>No results found</h3>
                <p>Try adjusting your search or filter to find what you're looking for.</p>
                {searchTerm && (
                  <button className="ta-button ta-button-secondary" onClick={() => setSearchTerm('')}>
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="ta-table-container">
                  <table className="ta-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Email</th>
                        <th>Exam</th>
                        <th>Gross WPM</th>
                        <th>Net WPM</th>
                        <th>Accuracy</th>
                        <th>Errors</th>
                        <th>Result</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.map((result) => (
                        <tr key={result.id}>
                          <td>
                            <div className="ta-user-info">
                              <div className="ta-user-avatar-small">
                                {result.student_name.charAt(0)}
                              </div>
                              <span
                                className="ta-user-name ta-clickable"
                                onClick={()=>fetchResultDetails(result)}
                              >
                                {result.student_name}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="ta-email">
                              <Mail size={14} />
                              <span>{result.email}</span>
                            </div>
                          </td>
                          <td>
                            <span className="ta-badge ta-badge-subtle">{result.exam_name}</span>
                          </td>
                          <td className="ta-numeric">{result.gross_wpm}</td>
                          <td className="ta-numeric ta-numeric-highlight">{result.net_wpm}</td>
                          <td className="ta-numeric">{result.accuracy}%</td>
                          <td className="ta-numeric">{result.errors}</td>
                          <td>
                            <span className={`ta-badge ta-badge-${result.result.toLowerCase()}`}>
                              {result.result === 'Pass' ? (
                                <><CheckCircle size={14} /> Pass</>
                              ) : (
                                <><XCircle size={14} /> Fail</>
                              )}
                            </span>
                          </td>
                          <td>
                            <div className="ta-date">
                              <CalendarDays size={14} />
                              <span>{formatDate(result.date)}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="ta-table-footer">
                  <div className="ta-table-info">
                    Showing <strong>{filteredResults.length}</strong> of <strong>{results.length}</strong> results
                  </div>
                  <div className="ta-pagination">
                    <button className="ta-pagination-button" disabled>Previous</button>
                    <span className="ta-pagination-current">1</span>
                    <button className="ta-pagination-button">Next</button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>

      {/* Modal - Modern Dialog Style */}
      {showModal && selectedResult && (
        <div className="ta-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ta-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ta-modal-header">
              <h2 className="ta-modal-title">Test Details</h2>
              <button className="ta-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="ta-modal-body">
              <ResultCard result={selectedResult} />
            </div>
            <div className="ta-modal-footer">
              <button
                className="ta-button ta-button-success"
                onClick={()=>updateResult(selectedResult.id,true)}
              >
                Mark Pass
              </button>
              <button
                className="ta-button ta-button-danger"
                onClick={()=>updateResult(selectedResult.id,false)}
              >
                Mark Fail
              </button>
              <button
                className="ta-button ta-button-secondary"
                onClick={()=>setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;